import { getCompressedAssetUrl } from 'lib/config/assetUrls';

const EARTH_GLOBE_ASSETS = '/web_v2/home-v4/world-sleep/earth/';

/** Bump when replacing globe snapshot, overlay, or earth maps at the same path. */
const EARTH_GLOBE_ASSET_VERSION = '4';

/** Floor at 4096 on desktop so SpeedSize does not UA-downscale; sources already ≤4050.
 *  On phones (<768px), floor at 1024 to avoid shipping 4K textures to mobile. */
const EARTH_GLOBE_TEXTURE_WIDTH_DESKTOP = 'w_4096';
const EARTH_GLOBE_TEXTURE_WIDTH_MOBILE = 'w_1024';

function earthGlobeSrc(src: string): string {
  const join = src.includes('?') ? '&' : '?';
  return `${src}${join}v=${EARTH_GLOBE_ASSET_VERSION}`;
}

function earthGlobeTextureUrl(
  file: string,
  width = EARTH_GLOBE_TEXTURE_WIDTH_DESKTOP,
): string {
  return `${getCompressedAssetUrl(
    earthGlobeSrc(`${EARTH_GLOBE_ASSETS}${file}`),
  )}/${width}`;
}

export const EARTH_GLOBE_SNAPSHOT_SRC = earthGlobeSrc(
  'web_v2/home-v4/world-sleep/globe-snapshot.webp',
);

export const EARTH_GLOBE_OVERLAY_SRC = getCompressedAssetUrl(
  earthGlobeSrc('/web_v2/home-v4/world-sleep/globe-overlay.svg'),
);

export function getEarthGlobeTextureUrls(
  isMobile = false,
): readonly [string, string, string] {
  const width = isMobile
    ? EARTH_GLOBE_TEXTURE_WIDTH_MOBILE
    : EARTH_GLOBE_TEXTURE_WIDTH_DESKTOP;
  return [
    earthGlobeTextureUrl('spec-map.webp', width),
    earthGlobeTextureUrl('clouds.webp', width),
    earthGlobeTextureUrl('night-lights.webp', width),
  ] as const;
}

export const EARTH_GLOBE_TEXTURE_URLS = getEarthGlobeTextureUrls(false);

type NetworkInformationLike = {
  saveData?: boolean;
};

function getConnection(): NetworkInformationLike | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

export function shouldUseEarthGlobeWebGL(): boolean {
  if (typeof window === 'undefined') return false;
  if (getConnection()?.saveData) return false;
  return true;
}

export function prefetchEarthGlobeTextures(): void {
  if (typeof document === 'undefined') return;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const urls = getEarthGlobeTextureUrls(isMobile);
  for (const url of urls) {
    const existing = document.head.querySelector(
      `link[rel="preload"][href="${url}"]`,
    );
    if (existing) continue;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  }
}
